import React, {useState, useEffect, useContext} from 'react';
import {Form, Radio, DatePicker} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import GenericDialog from '../../common/GenericDialog';
import dayjs from 'dayjs';
import {EDIT_ASSIGN_OBJECT} from '../teacher/enums';
import RecipientSelect, {RECIPIENT_GROUP_OBJ} from "../../common/RecipientSelect";
import axios from "../../common/axios";
import {UserContext} from "../../common/UserContext";

const AssignTestDialog = props => {
    const {open, onClose, setReload, selectedTests, isDiagnosticTest = false} = props;
    const {user} = useContext(UserContext);
    const [assignTestForm] = Form.useForm();
    const testIds = selectedTests.map(test => {return test?.testId});
    const [testType, setTestType] = useState(EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT);
    const [startDate, setStartDate] = useState(dayjs());
    const [openDialogDate, setOpenDialogDate] = useState(dayjs());
    const [recipientObj, setRecipientObj] = useState();

    // console.log('user',user);
    // console.log('user?.classes',user?.classes);

    const dialogWidth = isDiagnosticTest ? 600 : 550;
    const formLabelCol = isDiagnosticTest ? 9 : 7;
    const formWrapperCol = isDiagnosticTest ? 15 : 17;

    useEffect(() => {
        if (open) {
            setTestType(EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT);
            setOpenDialogDate(dayjs());
            assignTestForm.setFieldsValue({
                testType: EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT.val,
                note: '',
                showResultsOnExpiry: false,
                startDate: dayjs()
            });
        } else {
            setOpenDialogDate(null);
            assignTestForm.resetFields();
        }
    }, [open]);

    const onTestTypeChange = ({target: {value}}) => {
        setTestType(EDIT_ASSIGN_OBJECT.TEST_TYPE[value]);
    }

    const assignTests = async () => {
        assignTestForm.validateFields()
            .then(values => {
                const params = {testIds, ...values};

                params.startDate = params.startDate.format(user.getDateTimeFormatString());
                params.endDate = params.endDate.format(user.getDateTimeFormatString());

                if (RECIPIENT_GROUP_OBJ.STUDENTS.val !== recipientObj?.recipientGroup?.val) {
                    delete params.studentIds;
                }

                delete params.recipient;

                axios.post('/web/ui/test/teacher/assignTest', params, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(response => {
                        if (response.data.success) {
                            displayToastMessage('Test' + (testIds.length > 0 ? '/s' : '') + ' assigned successfully', null, 'success', undefined, 10000);

                            assignTestForm.resetFields();
                            if (typeof setReload === 'function') setReload();
                            if (typeof onClose === 'function') onClose();
                        } else {
                            displayToastMessage('Error assigning test', response.data.message, 'error', undefined, 10000);
                        }
                    }).catch(error => {
                        console.error(error);
                    });
            });
    }

    return (
        <GenericDialog
            title={'Assign test'}
            open={open}
            contentStyle={{width: dialogWidth}}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'},
                },
                {
                    name: 'Assign',
                    callback: assignTests,
                }
            ]}
        >
            <Form
                className={'ch-form'}
                form={assignTestForm}
                labelCol={{span: formLabelCol}}
                wrapperCol={{span: formWrapperCol}}
            >
                <Form.Item
                    label={'Choose format'}
                    name={'testType'}
                    extra={testType.extraTxt}
                >
                    <Radio.Group
                        buttonStyle={'solid'}
                        optionType={'button'}
                        onChange={onTestTypeChange}
                        options={[
                            {
                                label: EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT[isDiagnosticTest ? 'diagnosticName' : 'name'],
                                value: EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT.val,
                            },
                            {
                                label: EDIT_ASSIGN_OBJECT.TEST_TYPE.REVISION[isDiagnosticTest ? 'diagnosticName' : 'name'],
                                value: EDIT_ASSIGN_OBJECT.TEST_TYPE.REVISION.val,
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    label={'Start date'}
                    name={'startDate'}
                    extra={EDIT_ASSIGN_OBJECT.START_DATE_TOOLTIP}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a start date.',
                        },
                        {
                            validator: (_, value) => {
                                if (openDialogDate && value && value < openDialogDate) {
                                    return Promise.reject(`Start date must not be before (${openDialogDate.format(user.getDateFormatString() + ', HH:mm')}).`);
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement="topLeft"
                        onChange={setStartDate}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item
                    label={'End date'}
                    name={'endDate'}
                    extra={EDIT_ASSIGN_OBJECT.END_DATE_TOOLTIP}
                    rules={[
                        {
                            required: true,
                            message: 'Please select an end date.',
                        },
                        {
                            validator: (_, value) => {
                                const currentDate = dayjs(new Date().getTime()).add(10, 'minute').toDate();
                                if (value && startDate && value < startDate) {
                                    return Promise.reject('End date must be after start date.');
                                }
                                if (value && value < currentDate) {
                                    return Promise.reject('End date must be in more than 10 minutes from now.');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement="topLeft"
                        style={{width: '100%'}}
                    />
                </Form.Item>
                {isDiagnosticTest &&
                    <Form.Item label={'Allow students to view results'} name={'showResultsOnExpiry'}
                               extra={testType.val === EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT.val ? EDIT_ASSIGN_OBJECT.SHOW_RESULTS_ONE_SITTING_TOOLTIP : EDIT_ASSIGN_OBJECT.SHOW_RESULTS_MULTIPLE_SITTING_TOOLTIP}>
                        <Radio.Group
                            buttonStyle={'solid'}
                            optionType="button"
                            options={[
                                {label: 'Yes', value: true},
                                {label: 'No', value: false},
                            ]}
                        />
                    </Form.Item>
                }
                <Form.Item label={'Message for students'} name={'note'}>
                    <TextArea rows={4}/>
                </Form.Item>
                <RecipientSelect form={assignTestForm} classes={user.classes.filter(clazz => {return clazz.totalStudents && clazz.totalStudents > 0})} setRecipientObj={setRecipientObj} />
            </Form>
        </GenericDialog>
    );
};

export default AssignTestDialog;
